import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as SonnerToaster } from 'sonner';
import ServiceWorkerUpdateToast from '@/components/shared/ServiceWorkerUpdateToast';

// Layout
import Layout from '@/components/Layout';

// Pages
import Dashboard from '@/pages/Dashboard';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import TimeControl from '@/pages/TimeControl';
import MyHours from '@/pages/MyHours';
import Kiosk from '@/pages/Kiosk';
import Temperatures from '@/pages/Temperatures';
import ChlorineControl from '@/pages/ChlorineControl';
import Cleaning from '@/pages/Cleaning';
import Incidents from '@/pages/Incidents';
import Documents from '@/pages/Documents';
import Downloads from '@/pages/Downloads';
import Alerts from '@/pages/Alerts';
import Verifications from '@/pages/Verifications';
import RecipeSheets from '@/pages/RecipeSheets';
import Ingredients from '@/pages/Ingredients';
import ComplianceDashboard from '@/pages/ComplianceDashboard';
import APPCCManagement from '@/pages/APPCCManagement';
import GDPRManagement from '@/pages/GDPRManagement';
import PRLManagement from '@/pages/WorkerSafetyManagement';
import HarassmentProtocol from '@/pages/HarassmentProtocol';
import Analytics from '@/pages/Analytics';
import DocumentGenerator from '@/pages/DocumentGenerator';
import RegulatoryCompliance from '@/pages/RegulatoryCompliance';
import TechnicalCompliance from '@/pages/TechnicalCompliance';
import ElectricalCompliance from '@/pages/ElectricalCompliance';
import InvoiceScanner from '@/pages/InvoiceScanner';
import VenueProfile from '@/pages/VenueProfile';
import TeamManagement from '@/pages/TeamManagement';
import VenueSettings from '@/pages/VenueSettings';
import AdminVenues from '@/pages/AdminVenues';
import Templates from '@/pages/Templates';
import AlertSettings from '@/pages/AlertSettings';
import PlatformAdmin from '@/pages/PlatformAdmin';
import SelectPlan from '@/pages/SelectPlan';
import SubscriptionManagement from '@/pages/SubscriptionManagement';
import SubscriptionSuccess from '@/pages/SubscriptionSuccess';
import WorkerApp from '@/pages/WorkerApp';
import LandingPage from '@/pages/LandingPage';
import NFCClock from '@/pages/NFCClock';
import WorkerInvite from '@/pages/WorkerInvite';
import PestControl from '@/pages/PestControl';
import Maintenance from '@/pages/Maintenance';
import Traceability from '@/pages/Traceability';
import RiskDashboard from '@/pages/RiskDashboard';
import AIAssistant from '@/pages/AIAssistant';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import AdminBackups from '@/pages/AdminBackups';
import CostReview from '@/pages/CostReview';
import SyncStatus from '@/pages/SyncStatus';
import RGPDCenter from '@/pages/RGPDCenter';
import WasteManagement from '@/pages/WasteManagement';
import HosysAIDashboard from '@/pages/hosysai/HosysAIDashboard';
import HosysAIDiagnostic from '@/pages/hosysai/HosysAIDiagnostic';
import HosysAISystems from '@/pages/hosysai/HosysAISystems';
import HosysAIRisk from '@/pages/hosysai/HosysAIRisk';
import HosysAIIncidents from '@/pages/hosysai/HosysAIIncidents';
import HosysAILanding from '@/pages/hosysai/HosysAILanding';
import HosysAILogin from '@/pages/hosysai/HosysAILogin';
import HosysAIRegister from '@/pages/hosysai/HosysAIRegister';
import HosysAICourses from '@/pages/hosysai/HosysAICourses';

import { OfflineBanner } from '@/components/OfflineBanner';

import '@/index.css';

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoadingAuth } = useAuth();
  const location = useLocation();

  if (isLoadingAuth) {
    return (
      <div className="min-h-screen bg-[#f5f6fa] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#7B2D3F]/20 border-t-[#7B2D3F] rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <Layout currentPageName={location.pathname.slice(1)}>{children}</Layout>;
}

// Public route wrapper
function PublicRoute({ children }) {
  const { isAuthenticated, isLoadingAuth } = useAuth();

  if (isLoadingAuth) {
    return (
      <div className="min-h-screen bg-[#f5f6fa] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#7B2D3F]/20 border-t-[#7B2D3F] rounded-full animate-spin"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={isHosysAiMode() ? "/HosysAI" : "/Dashboard"} replace />;
  }

  return children;
}

// Auth callback for Google OAuth
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AuthCallback() {
  const { exchangeSession } = useAuth();
  const location = useLocation();
  const hasProcessed = React.useRef(false);

  React.useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        const hash = location.hash;
        const params = new URLSearchParams(hash.replace('#', ''));
        const sessionId = params.get('session_id');

        if (sessionId) {
          await exchangeSession(sessionId);
          window.history.replaceState(null, '', '/Dashboard');
          window.location.href = '/Dashboard';
        } else {
          window.location.href = '/login';
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        window.location.href = '/login';
      }
    };

    processAuth();
  }, []);

  return (
    <div className="min-h-screen bg-[#f5f6fa] flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-[#7B2D3F]/20 border-t-[#7B2D3F] rounded-full animate-spin"></div>
    </div>
  );
}

// Detect if we're in HOSYS AI standalone mode (separate frontend deployment)
function isHosysAiMode() {
  if (typeof window === "undefined") return false;
  const envMode = (process.env.REACT_APP_MODE || "").toLowerCase();
  if (envMode === "hosysai" || envMode === "ai") return true;
  const host = window.location.hostname;
  if (host === "ai.ho-sys.com" || host.startsWith("ai.")) return true;
  // Dev/preview override via URL query or localStorage for testing
  try {
    if (typeof localStorage !== "undefined" && localStorage.getItem("hosysai_mode") === "1") return true;
    const params = new URLSearchParams(window.location.search);
    if (params.get("hosysai") === "1") {
      localStorage.setItem("hosysai_mode", "1");
      return true;
    }
  } catch (e) { /* noop */ }
  return false;
}

// Landing route - shows landing for unauthenticated, dashboard for authenticated
function LandingRoute() {
  const { isAuthenticated, isLoadingAuth } = useAuth();

  if (isLoadingAuth) {
    return (
      <div className="min-h-screen bg-[#f5f6fa] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#7B2D3F]/20 border-t-[#7B2D3F] rounded-full animate-spin"></div>
      </div>
    );
  }

  // HOSYS AI mode: show its own landing / redirect to /HosysAI if authenticated
  if (isHosysAiMode()) {
    if (isAuthenticated) {
      return <Navigate to="/HosysAI" replace />;
    }
    return <HosysAILanding />;
  }

  if (isAuthenticated) {
    return <Navigate to="/Dashboard" replace />;
  }

  return <LandingPage />;
}

// App router with session_id detection and PWA deep link handling
function AppRouter() {
  const location = useLocation();
  const navigate = React.useCallback((to) => window.location.href = to, []);
  const hosysAiMode = isHosysAiMode();

  // Handle PWA deep links when app regains focus
  React.useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const currentPath = window.location.pathname + window.location.search;
        if (currentPath.includes('/NFCClock') || currentPath.includes('/Kiosk') || currentPath.includes('/WorkerInvite')) {
          if (location.pathname !== window.location.pathname) {
            window.location.reload();
          }
        }
      }
    };

    // Handle launch queue for PWA deep links
    if ('launchQueue' in window) {
      window.launchQueue.setConsumer((launchParams) => {
        if (launchParams.targetURL) {
          const url = new URL(launchParams.targetURL);
          if (url.pathname !== location.pathname) {
            window.location.href = launchParams.targetURL;
          }
        }
      });
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [location.pathname]);

  // Detect session_id for Google OAuth callback
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // HOSYS AI standalone mode: reduced route set
  if (hosysAiMode) {
    return (
      <Routes>
        <Route path="/" element={<LandingRoute />} />
        <Route path="/login" element={<PublicRoute><HosysAILogin /></PublicRoute>} />
        <Route path="/Login" element={<PublicRoute><HosysAILogin /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><HosysAIRegister /></PublicRoute>} />
        <Route path="/Register" element={<PublicRoute><HosysAIRegister /></PublicRoute>} />
        <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* HOSYS AI protected routes */}
        <Route path="/HosysAI" element={<ProtectedRoute><HosysAIDashboard /></ProtectedRoute>} />
        <Route path="/HosysAI/Diagnostic" element={<ProtectedRoute><HosysAIDiagnostic /></ProtectedRoute>} />
        <Route path="/HosysAI/Systems" element={<ProtectedRoute><HosysAISystems /></ProtectedRoute>} />
        <Route path="/HosysAI/Risk" element={<ProtectedRoute><HosysAIRisk /></ProtectedRoute>} />
        <Route path="/HosysAI/Incidents" element={<ProtectedRoute><HosysAIIncidents /></ProtectedRoute>} />
        <Route path="/HosysAI/Courses" element={<ProtectedRoute><HosysAICourses /></ProtectedRoute>} />
        <Route path="/HosysAI/MyCourses" element={<ProtectedRoute><HosysAICourses /></ProtectedRoute>} />

        {/* Redirect anything else back to landing (or /HosysAI if logged in) */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingRoute />} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/Login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/Register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/Kiosk" element={<Kiosk />} />
      <Route path="/NFCClock" element={<NFCClock />} />
      <Route path="/WorkerInvite" element={<WorkerInvite />} />

      {/* Protected routes */}
      <Route path="/Dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/TimeControl" element={<ProtectedRoute><TimeControl /></ProtectedRoute>} />
      <Route path="/MyHours" element={<ProtectedRoute><MyHours /></ProtectedRoute>} />
      <Route path="/Temperatures" element={<ProtectedRoute><Temperatures /></ProtectedRoute>} />
      <Route path="/ChlorineControl" element={<ProtectedRoute><ChlorineControl /></ProtectedRoute>} />
      <Route path="/Cleaning" element={<ProtectedRoute><Cleaning /></ProtectedRoute>} />
      <Route path="/Incidents" element={<ProtectedRoute><Incidents /></ProtectedRoute>} />
      <Route path="/Documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
      <Route path="/Downloads" element={<ProtectedRoute><Downloads /></ProtectedRoute>} />
      <Route path="/Alerts" element={<ProtectedRoute><Alerts /></ProtectedRoute>} />
      <Route path="/Verifications" element={<ProtectedRoute><Verifications /></ProtectedRoute>} />
      <Route path="/RecipeSheets" element={<ProtectedRoute><RecipeSheets /></ProtectedRoute>} />
      <Route path="/Ingredients" element={<ProtectedRoute><Ingredients /></ProtectedRoute>} />
      <Route path="/ComplianceDashboard" element={<ProtectedRoute><ComplianceDashboard /></ProtectedRoute>} />
      <Route path="/APPCCManagement" element={<ProtectedRoute><APPCCManagement /></ProtectedRoute>} />
      <Route path="/GDPRManagement" element={<ProtectedRoute><GDPRManagement /></ProtectedRoute>} />
      <Route path="/PRLManagement" element={<ProtectedRoute><PRLManagement /></ProtectedRoute>} />
      <Route path="/HarassmentProtocol" element={<ProtectedRoute><HarassmentProtocol /></ProtectedRoute>} />
      <Route path="/Analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
      <Route path="/DocumentGenerator" element={<ProtectedRoute><DocumentGenerator /></ProtectedRoute>} />
      <Route path="/RegulatoryCompliance" element={<ProtectedRoute><RegulatoryCompliance /></ProtectedRoute>} />
      <Route path="/TechnicalCompliance" element={<ProtectedRoute><TechnicalCompliance /></ProtectedRoute>} />
      <Route path="/ElectricalCompliance" element={<ProtectedRoute><ElectricalCompliance /></ProtectedRoute>} />
      <Route path="/InvoiceScanner" element={<ProtectedRoute><InvoiceScanner /></ProtectedRoute>} />
      <Route path="/VenueProfile" element={<ProtectedRoute><VenueProfile /></ProtectedRoute>} />
      <Route path="/TeamManagement" element={<ProtectedRoute><TeamManagement /></ProtectedRoute>} />
      <Route path="/VenueSettings" element={<ProtectedRoute><VenueSettings /></ProtectedRoute>} />
      <Route path="/AdminVenues" element={<ProtectedRoute><AdminVenues /></ProtectedRoute>} />
      <Route path="/Templates" element={<ProtectedRoute><Templates /></ProtectedRoute>} />
      <Route path="/AlertSettings" element={<ProtectedRoute><AlertSettings /></ProtectedRoute>} />
      <Route path="/PlatformAdmin" element={<ProtectedRoute><PlatformAdmin /></ProtectedRoute>} />
      <Route path="/SelectPlan" element={<ProtectedRoute><SelectPlan /></ProtectedRoute>} />
      <Route path="/SubscriptionManagement" element={<ProtectedRoute><SubscriptionManagement /></ProtectedRoute>} />
      <Route path="/SubscriptionSuccess" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />
      <Route path="/WorkerApp" element={<ProtectedRoute><WorkerApp /></ProtectedRoute>} />
      <Route path="/PestControl" element={<ProtectedRoute><PestControl /></ProtectedRoute>} />
      <Route path="/Maintenance" element={<ProtectedRoute><Maintenance /></ProtectedRoute>} />
      <Route path="/Traceability" element={<ProtectedRoute><Traceability /></ProtectedRoute>} />
      <Route path="/RiskDashboard" element={<ProtectedRoute><RiskDashboard /></ProtectedRoute>} />
      <Route path="/AIAssistant" element={<ProtectedRoute><AIAssistant /></ProtectedRoute>} />
      <Route path="/AdminBackups" element={<ProtectedRoute><AdminBackups /></ProtectedRoute>} />
      <Route path="/CostReview" element={<ProtectedRoute><CostReview /></ProtectedRoute>} />
      <Route path="/SyncStatus" element={<ProtectedRoute><SyncStatus /></ProtectedRoute>} />
      <Route path="/RGPDCenter" element={<ProtectedRoute><RGPDCenter /></ProtectedRoute>} />
      <Route path="/WasteManagement" element={<ProtectedRoute><WasteManagement /></ProtectedRoute>} />

      {/* HOSYS AI — Academy & Compliance (custom layout, no main Layout wrapper) */}
      <Route path="/HosysAI" element={<ProtectedRoute><HosysAIDashboard /></ProtectedRoute>} />
      <Route path="/HosysAI/Diagnostic" element={<ProtectedRoute><HosysAIDiagnostic /></ProtectedRoute>} />
      <Route path="/HosysAI/Systems" element={<ProtectedRoute><HosysAISystems /></ProtectedRoute>} />
      <Route path="/HosysAI/Risk" element={<ProtectedRoute><HosysAIRisk /></ProtectedRoute>} />
      <Route path="/HosysAI/Incidents" element={<ProtectedRoute><HosysAIIncidents /></ProtectedRoute>} />

      {/* Default redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AppRouter />
        <Toaster />
        <SonnerToaster position="top-right" richColors />
        <OfflineBanner />
        <ServiceWorkerUpdateToast />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
